import { compact } from 'lodash'
import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PER_PAGE } from '@const/pagination'
import useSearchParam from '@hooks/useSearchParam'
import Pagination from '@templates/Pagination'
import { ListPageLayoutListProps } from '@templates/ListPageLayout'
import EmptyList from './EmptyList'
import { ItemsWrapper, ListWrapper, Wrapper } from './styled'

export type ListItemProps<Item, Additional = any> = {
  item: Item
  index: number
  isLoading: boolean
  additionalPropsForItem: Additional
  handleClickOnItem: () => void
  opened: boolean
}

type Props = ListPageLayoutListProps

const List: FC<Props> = ({
  ItemComponent,
  list,
  perPage = PER_PAGE,
  perLine = 1,
  isLoading = false,
  length,
  page,
  setPage,
  additionalPropsForItem,
  onRefresh,
  scrollToElementId,
}) => {
  const openedQuery = useSearchParam('opened')
  const initialOpened = openedQuery ? [openedQuery] : []
  const [opened, setOpened] = useState<string[]>(initialOpened)
  const [localPage, setLocalPage] = useState(page || 1)
  const currentPage = page ?? localPage
  const location = useLocation()
  const { hash } = location

  useEffect(() => {
    if (page) {
      setLocalPage(page)
    }
  }, [page])

  useEffect(() => {
    if (!hash || !hash.startsWith('#')) {
      return
    }

    const timeout = setTimeout(() => {
      const id = decodeURIComponent(hash.slice(1))
      const element = document.querySelector(`#${CSS.escape(id)}`)

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [hash, list, opened, currentPage, isLoading])

  // Не сбрасывать opened при фоновом refetch (мутации вроде reorder тем инвалидируют список).
  // Иначе isLoading на мгновение true → все карточки «схлопываются» и ломают работу внутри раскрытого курса.

  const handleClickOnItem = (index: number) => {
    const targetId = String(index)

    setOpened((prev) => {
      const findedIndex = prev.indexOf(targetId)
      if (findedIndex > -1) {
        delete prev[findedIndex]
        return compact(prev)
      } else {
        return [...prev, targetId]
      }
    })
  }

  const handleChangePagination = (newPage: number) => {
    setOpened([])
    setLocalPage(newPage)
    setPage?.(newPage)
    if (scrollToElementId) {
      document
        .querySelector(`#${scrollToElementId}`)
        ?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  let arrayIndex = 0

  const loadingList: Record<string, unknown>[] = []
  for (let i = 0; i < perPage * perLine; i++) {
    loadingList.push({})
  }

  const hasListData = Array.isArray(list) && list.length > 0
  // Только при первой загрузке без данных подменяем список скелетонами.
  // При refetch (инвалидация после reorder тем и т.д.) isLoading/refreshStarted могут стать true,
  // но list ещё из кэша — иначе resultKey становится 1,2,3… вместо id курса, opened ломается,
  // а MyCourseItem с isLoading рендерит Skeleton и размонтирует Body → «схлопываются» разделы.
  const loadingPlaceholders = isLoading && !hasListData
  const resultList = loadingPlaceholders ? loadingList : (list ?? [])

  const perLinesFiltered: any[][] = resultList.reduce(
    (acc, item, index) => {
      if (index > 0 && index % perLine === 0) {
        arrayIndex = arrayIndex + 1
        acc[arrayIndex] = []
      }

      acc[arrayIndex].push(item)
      return acc
    },
    [[]] as any[][]
  )

  if (
    !loadingPlaceholders &&
    (!perLinesFiltered ||
      perLinesFiltered.length === 0 ||
      perLinesFiltered[0]?.length === 0)
  ) {
    return <EmptyList onRefresh={onRefresh} />
  }

  if (!ItemComponent) {
    return 'Не задан компонент списка'
  }

  return (
    <Wrapper id="list">
      <ListWrapper>
        {perLinesFiltered.map((line, i) => (
          <ItemsWrapper key={i + 1}>
            {line.map((item, index) => {
              const hydraId =
                typeof item['@id'] === 'string' ? item['@id'] : undefined
              const idFromHydra =
                typeof hydraId?.split('/')?.at(-1) === 'string'
                  ? Number(hydraId?.split('/')?.at(-1))
                  : undefined

              const resultKey =
                (item.id as number) || idFromHydra || (index + 1) * (i + 1)
              const normalizedResultKey = String(resultKey)

              return (
                <ItemComponent
                  item={item}
                  index={i}
                  key={resultKey}
                  isLoading={loadingPlaceholders}
                  additionalPropsForItem={additionalPropsForItem}
                  handleClickOnItem={() => handleClickOnItem(resultKey)}
                  opened={opened.includes(normalizedResultKey)}
                />
              )
            })}
          </ItemsWrapper>
        ))}
      </ListWrapper>
      <Pagination
        page={currentPage}
        itemsCount={length}
        perPage={perPage}
        onChangePagination={handleChangePagination}
      />
    </Wrapper>
  )
}

export default List
